import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import {
  PenLine,
  TextCursorInput,
  User,
  Briefcase,
  CalendarDays,
  MapPin,
  Building2,
  Mail,
  Landmark,
  CheckCircle,
} from 'lucide-react';

export interface SignableFieldData {
  id: string;
  type: string;
  label: string;
  x: number;
  y: number;
  pageNumber: number;
  width?: number;
  height?: number;
  recipientId?: string;
  /** The value filled by the signer (text for most fields, base64 for signature) */
  value?: string | null;
  /** Whether this field belongs to the current signer */
  isSignerField?: boolean;
  /** Signature image data URL when type=signature and completed */
  signatureImage?: string | null;
  /** Full name fallback for signature display */
  fullName?: string | null;
}

interface SignableFieldProps {
  field: SignableFieldData;
  onClick?: (field: SignableFieldData) => void;
}

const fieldIcons: Record<string, React.ElementType> = {
  signature: PenLine,
  initial: TextCursorInput,
  name: User,
  email: Mail,
  date: CalendarDays,
  city: MapPin,
  province: Building2,
  state: Landmark,
  position: Briefcase,
};

const FIELD_TYPE_KEYS: Record<string, string> = {
  signature: 'viewer_field_signature',
  initial: 'viewer_field_initial',
  date: 'viewer_field_date',
  name: 'viewer_field_name',
  email: 'viewer_field_email',
  city: 'viewer_field_city',
  position: 'viewer_field_position',
  province: 'viewer_field_province',
  state: 'viewer_field_state',
};

const SignableField: React.FC<SignableFieldProps> = ({ field, onClick }) => {
  const { t } = useTranslation(['documents']);
  const isCompleted = Boolean(field.value || field.signatureImage);
  const isSigner = field.isSignerField !== false;
  const Icon = fieldIcons[field.type] || User;

  const handleClick = () => {
    if (isSigner && !isCompleted && onClick) {
      onClick(field);
    }
  };

  // Render completed signature field
  if (isCompleted && field.type === 'signature') {
    return (
      <div className="relative w-full h-full">
        <div className="w-full h-full flex items-center justify-center bg-green-50/80 border-2 border-green-300 rounded-md overflow-hidden">
          {field.signatureImage ? (
            <img
              src={field.signatureImage}
              alt="Signature"
              className="max-w-full max-h-full object-contain"
            />
          ) : field.fullName ? (
            <span
              className="text-primary"
              style={{ fontFamily: "'Dancing Script', cursive", fontSize: '1.25rem' }}
            >
              {field.fullName}
            </span>
          ) : (
            <span className="text-sm text-green-700">{field.value}</span>
          )}
        </div>
        <CheckCircle className="absolute -top-1.5 -right-1.5 h-4 w-4 text-green-600 bg-white rounded-full" />
      </div>
    );
  }

  // Render completed non-signature field
  if (isCompleted) {
    return (
      <div className="relative w-full h-full">
        <div className="w-full h-full flex items-center gap-1.5 px-2 bg-green-50/80 border-2 border-green-300 rounded-md">
          <Icon className="h-3.5 w-3.5 text-green-600 shrink-0" />
          <span className="text-sm text-green-800 truncate">{field.value}</span>
        </div>
        <CheckCircle className="absolute -top-1.5 -right-1.5 h-4 w-4 text-green-600 bg-white rounded-full" />
      </div>
    );
  }

  // Render unfilled signer field (clickable)
  if (isSigner) {
    return (
      <div
        className={cn(
          'w-full h-full flex items-center gap-1.5 px-2 rounded-md border-2 border-dashed cursor-pointer transition-all',
          'border-blue-400 bg-blue-50/80 hover:bg-blue-100/90 hover:border-blue-500',
          'animate-pulse hover:animate-none'
        )}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); }}
      >
        <Icon className="h-4 w-4 text-blue-600 shrink-0" />
        <span className="text-xs font-medium text-blue-700 truncate">
          {t(`documents:${FIELD_TYPE_KEYS[field.type]}`, { defaultValue: field.label })}
        </span>
      </div>
    );
  }

  // Render non-signer field (read-only, greyed out)
  return (
    <div className="w-full h-full flex items-center gap-1.5 px-2 rounded-md border bg-secondary/30 border-border">
      <Icon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
      <span className="text-xs text-muted-foreground truncate">
        {t(`documents:${FIELD_TYPE_KEYS[field.type]}`, { defaultValue: field.label })}
      </span>
    </div>
  );
};

export default SignableField;
